import React from "react";
import { Edges } from "@react-three/drei";
import { DoubleSide, Texture } from "three";
import {
  MM,
  PORTINA_ALTEZZA,
  PORTINA_CORNICE_W,
  FINESTRA_LARGHEZZA,
  FINESTRA_ALTEZZA,
  C_BORDO_CHIARO,
  IntegratedDoorPosition,
} from "./constants";

// ====================== HELPER ======================
export function getPortinaX(
  posizione: IntegratedDoorPosition,
  larghezza: number,
  portinaLarghezza: number
): number {
  if (posizione === "sx") return -larghezza / 2 + MM(420) + portinaLarghezza / 2;
  if (posizione === "dx") return  larghezza / 2 - MM(420) - portinaLarghezza / 2;
  return 0;
}

// Restituisce true se lo slot della finestra sovrappone i bordi della cornice portina.
// slotLarghezza/slotAltezza sono le misure reali dell'elemento da posizionare (finestra
// 230x230 o 400x360, griglia 344x138 o 400x200): misurare sempre con la finestra piu'
// grande bloccherebbe slot che in realta' stanno dentro il vano della portina.
export function slotOverlapsPortina(
  wx: number,
  panelWorldY: number,
  portinaX: number,
  portinaLarghezza: number,
  slotLarghezza: number = FINESTRA_LARGHEZZA,
  slotAltezza: number = FINESTRA_ALTEZZA
): boolean {
  const outerLeft   = portinaX - portinaLarghezza / 2;
  const outerRight  = portinaX + portinaLarghezza / 2;
  const outerBottom = 0;
  const outerTop    = PORTINA_ALTEZZA;

  const winLeft   = wx - slotLarghezza / 2;
  const winRight  = wx + slotLarghezza / 2;
  const winBottom = panelWorldY - slotAltezza / 2;
  const winTop    = panelWorldY + slotAltezza / 2;

  const overlapsOuter =
    winLeft < outerRight && winRight > outerLeft &&
    winBottom < outerTop && winTop > outerBottom;
  if (!overlapsOuter) return false;

  const innerLeft   = outerLeft   + PORTINA_CORNICE_W;
  const innerRight  = outerRight  - PORTINA_CORNICE_W;
  const innerBottom = outerBottom  + PORTINA_CORNICE_W;
  const innerTop    = outerTop    - PORTINA_CORNICE_W;

  const fitsInner =
    winLeft >= innerLeft && winRight <= innerRight &&
    winBottom >= innerBottom && winTop <= innerTop;
  return !fitsInner;
}


/**
 * Allinea gli slot di un pannello alla portina integrata.
 *
 * Gli slot sono distribuiti a passo fisso sulla larghezza del pannello e non sanno che
 * esiste la portina: quando la portina e' laterale (sx/dx) nessuno slot cade allineato al
 * vano, quindi una finestra che ci starebbe comodamente risultava comunque non
 * posizionabile. Qui, tra gli slot che intersecano la portina, quello piu' vicino all'asse
 * del vano viene ricentrato sulla portina se l'elemento ci sta dentro la cornice; gli
 * altri restano bloccati perche' andrebbero a cavallo del telaio.
 *
 * Restituisce, per ogni slot: la X da usare, oppure null se lo slot non e' posizionabile.
 */
export function allineaSlotAllaPortina(
  xs: number[],
  panelWorldY: number,
  portinaX: number,
  portinaLarghezza: number,
  slotLarghezza: number,
  slotAltezza: number
): (number | null)[] {
  const outerLeft   = portinaX - portinaLarghezza / 2;
  const outerRight  = portinaX + portinaLarghezza / 2;
  const outerBottom = 0;
  const outerTop    = PORTINA_ALTEZZA;

  const slotBottom = panelWorldY - slotAltezza / 2;
  const slotTop    = panelWorldY + slotAltezza / 2;

  const intersecano = xs
    .map((x, i) => ({ x, i }))
    .filter(({ x }) =>
      x - slotLarghezza / 2 < outerRight && x + slotLarghezza / 2 > outerLeft &&
      slotBottom < outerTop && slotTop > outerBottom
    );

  if (intersecano.length === 0) return xs.map(x => x);

  const innerLeft   = outerLeft   + PORTINA_CORNICE_W;
  const innerRight  = outerRight  - PORTINA_CORNICE_W;
  const innerBottom = outerBottom + PORTINA_CORNICE_W;
  const innerTop    = outerTop    - PORTINA_CORNICE_W;

  // L'elemento entra nel vano della portina? La cornice e' simmetrica, quindi l'asse del
  // vano coincide con portinaX.
  const entraNelVano =
    slotLarghezza <= innerRight - innerLeft &&
    slotBottom >= innerBottom && slotTop <= innerTop;

  // Un solo elemento nel vano: prendiamo lo slot che si sposta di meno.
  const daRicentrare = entraNelVano
    ? intersecano.reduce((best, cur) =>
        Math.abs(cur.x - portinaX) < Math.abs(best.x - portinaX) ? cur : best
      ).i
    : -1;

  return xs.map((x, i) => {
    if (!intersecano.some(s => s.i === i)) return x;
    return i === daRicentrare ? portinaX : null;
  });
}

// ====================== COMPONENTE ======================
export type PortinaProps = {
  spessore: number;
  altezza: number;
  portinaX: number;
  portinaLarghezza: number;
  colore: Texture | undefined;
  coloreInterno: Texture | undefined;
  manigliaColore: string;
  manigliaColoreInterno: string;
  apertura: "sx" | "dx";
  sogliaAltezza: number;
};

export const Portina: React.FC<PortinaProps> = ({
  spessore,
  altezza,
  portinaX,
  portinaLarghezza,
  colore: texture,
  coloreInterno: textureInterno,
  manigliaColore,
  manigliaColoreInterno,
  apertura,
  sogliaAltezza,
}) => {
  const sogliaH     = MM(sogliaAltezza);
  const effectiveH  = PORTINA_ALTEZZA - sogliaH;        // altezza netta apertura
  const cw          = PORTINA_CORNICE_W;
  const hw          = portinaLarghezza / 2;
  const hh          = effectiveH / 2;
  // gruppo spostato in su di sogliaH/2 così il top rimane fisso a PORTINA_ALTEZZA dal pavimento
  const posY        = sogliaH + effectiveH / 2 - altezza / 2;
  const zOff        = spessore / 2 + MM(2);
  const zOffInterno = -(spessore / 2 + MM(2));
  const s           = apertura === "sx" ? 1 : -1;

  const corniceExt = (tex: Texture | undefined) => (
    <meshStandardMaterial map={tex} color={tex ? "#ffffff" : "#888888"} side={DoubleSide} />
  );

  return (
    <group position={[portinaX, posY, 0]}>
      {/* ── Cornice ESTERNA ── */}
      <mesh position={[0, hh - cw / 2, zOff]}>
        <boxGeometry args={[portinaLarghezza, cw, MM(8)]} />
        {corniceExt(texture)}<Edges color={C_BORDO_CHIARO} />
      </mesh>
      <mesh position={[0, -hh + cw / 2, zOff]}>
        <boxGeometry args={[portinaLarghezza, cw, MM(8)]} />
        {corniceExt(texture)}<Edges color={C_BORDO_CHIARO} />
      </mesh>
      <mesh position={[-hw + cw / 2, 0, zOff]}>
        <boxGeometry args={[cw, effectiveH, MM(8)]} />
        {corniceExt(texture)}<Edges color={C_BORDO_CHIARO} />
      </mesh>
      <mesh position={[hw - cw / 2, 0, zOff]}>
        <boxGeometry args={[cw, effectiveH, MM(8)]} />
        {corniceExt(texture)}<Edges color={C_BORDO_CHIARO} />
      </mesh>

      {/* ── Cornice INTERNA ── */}
      <mesh position={[0, hh - cw / 2, zOffInterno]}>
        <boxGeometry args={[portinaLarghezza, cw, MM(8)]} />
        {corniceExt(textureInterno)}<Edges color={C_BORDO_CHIARO} />
      </mesh>
      <mesh position={[0, -hh + cw / 2, zOffInterno]}>
        <boxGeometry args={[portinaLarghezza, cw, MM(8)]} />
        {corniceExt(textureInterno)}<Edges color={C_BORDO_CHIARO} />
      </mesh>
      <mesh position={[-hw + cw / 2, 0, zOffInterno]}>
        <boxGeometry args={[cw, effectiveH, MM(8)]} />
        {corniceExt(textureInterno)}<Edges color={C_BORDO_CHIARO} />
      </mesh>
      <mesh position={[hw - cw / 2, 0, zOffInterno]}>
        <boxGeometry args={[cw, effectiveH, MM(8)]} />
        {corniceExt(textureInterno)}<Edges color={C_BORDO_CHIARO} />
      </mesh>
      

      {/* ── Maniglia ESTERNA ── */}
      <group position={[s * (hw - cw - MM(50)), 0, spessore / 2 + MM(6)]} scale={[s, 1, 1]}>
        <mesh position={[0, 0, MM(4)]} rotation={[-Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[MM(30), MM(30), MM(8), 32]} />
          <meshStandardMaterial color={manigliaColore} metalness={0.9} roughness={0.15} side={DoubleSide} />
        </mesh>
        <mesh position={[0, 0, MM(17)]} rotation={[-Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[MM(10), MM(13), MM(18), 16]} />
          <meshStandardMaterial color={manigliaColore} metalness={0.9} roughness={0.1} side={DoubleSide} />
        </mesh>
        <group rotation={[0, 0, 0.18]}>
          <mesh position={[-MM(45), 0, MM(24)]} rotation={[0, 0, Math.PI / 2]}>
            <cylinderGeometry args={[MM(6), MM(10), MM(90), 16]} />
            <meshStandardMaterial color={manigliaColore} metalness={0.92} roughness={0.07} side={DoubleSide} />
          </mesh>
          <mesh position={[-MM(90), 0, MM(24)]}>
            <sphereGeometry args={[MM(6), 16, 12]} />
            <meshStandardMaterial color={manigliaColore} metalness={0.92} roughness={0.07} side={DoubleSide} />
          </mesh>
        </group>
        <mesh position={[0, -MM(100), MM(3)]} rotation={[-Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[MM(20), MM(20), MM(6), 32]} />
          <meshStandardMaterial color={manigliaColore} metalness={0.9} roughness={0.15} side={DoubleSide} />
        </mesh>
      </group>

      {/* ── Maniglia INTERNA (stessa posizione fisica, sporge verso -z) ── */}
      <group position={[s * (hw - cw - MM(50)), 0, -(spessore / 2 + MM(6))]} scale={[s, 1, -1]}>
        <mesh position={[0, 0, MM(4)]} rotation={[-Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[MM(30), MM(30), MM(8), 32]} />
          <meshStandardMaterial color={manigliaColoreInterno} metalness={0.9} roughness={0.15} side={DoubleSide} />
        </mesh>
        <mesh position={[0, 0, MM(17)]} rotation={[-Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[MM(10), MM(13), MM(18), 16]} />
          <meshStandardMaterial color={manigliaColoreInterno} metalness={0.9} roughness={0.1} side={DoubleSide} />
        </mesh>
        <group rotation={[0, 0, 0.18]}>
          <mesh position={[-MM(45), 0, MM(24)]} rotation={[0, 0, Math.PI / 2]}>
            <cylinderGeometry args={[MM(6), MM(10), MM(90), 16]} />
            <meshStandardMaterial color={manigliaColoreInterno} metalness={0.92} roughness={0.07} side={DoubleSide} />
          </mesh>
          <mesh position={[-MM(90), 0, MM(24)]}>
            <sphereGeometry args={[MM(6), 16, 12]} />
            <meshStandardMaterial color={manigliaColoreInterno} metalness={0.92} roughness={0.07} side={DoubleSide} />
          </mesh>
        </group>
        <mesh position={[0, -MM(100), MM(3)]} rotation={[-Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[MM(20), MM(20), MM(6), 32]} />
          <meshStandardMaterial color={manigliaColoreInterno} metalness={0.9} roughness={0.15} side={DoubleSide} />
        </mesh>
      </group>
    </group>
  );
};
